import { Eye, EyeOff, Search } from "lucide-react";
import { useState } from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { commonIconProps16 } from "@/utils/constants";

import type { InputProps } from "./types";
import { inputVariants } from "./variants";

export interface ExtraButtonProps {
  text: string;
  onClick: () => void;
  testId?: string;
  isPercent?: boolean;
  disabled?: boolean;
}

export const CustomInput = ({
  className,
  variant,
  children,
  type = "text",
  extraButton,
  isPercent = false,
  ...props
}: InputProps & { extraButton?: ExtraButtonProps; isPercent?: boolean }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const isSearch = type === "search";

  return (
    <div className="relative">
      {isSearch && (
        <Search
          {...commonIconProps16}
          color="#B0B0B0"
          className="absolute top-3 left-3 pointer-events-none"
        />
      )}
      <Input
        className={cn(
          inputVariants({ variant }),
          isSearch && "pl-9",
          className
        )}
        type={isPassword ? (showPassword ? "text" : "password") : type}
        {...props}
      >
        {children}
      </Input>
      {isPercent && (
        <span
          className={cn(
            "absolute end-8 top-2.5 text-sm",
            props.disabled && "opacity-50"
          )}
        >
          %
        </span>
      )}
      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword(prev => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          data-testid="show-hide-password-button"
        >
          {showPassword ? (
            <EyeOff className="w-5 h-5" />
          ) : (
            <Eye className="w-5 h-5" />
          )}
        </button>
      )}
      {extraButton && (
        <button
          type="button"
          onClick={extraButton.onClick}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-500 bg-white px-2 py-1 font-bold text-sm cursor-pointer hover:bg-blue-50 rounded transition duration-200 disabled:text-[#94A3B8] disabled:hover:bg-transparent"
          data-testid={extraButton.testId ?? "custom-input-extra-button"}
          disabled={extraButton.disabled}
        >
          {extraButton.text}
        </button>
      )}
    </div>
  );
};
